import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Beaker, ShieldCheck, Stethoscope, ExternalLink } from "lucide-react";

const STORE_FALLBACK_URL = "https://www.badi.com.au/?utm_source=badi-app&utm_medium=rx-fallback&utm_campaign=script-request";

const DOSAGE_OPTIONS = [
  { value: "capsule",     label: "Capsule" },
  { value: "tincture",    label: "Tincture / liquid" },
  { value: "powder",      label: "Powder" },
  { value: "cream-topical", label: "Cream / topical" },
  { value: "no-preference", label: "No preference — recommend best form" },
];

export default function PrescriptionRequest() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    goals: "",
    symptoms: "",
    current_supplements: "",
    allergies: "",
    dosage_preference: "",
    notes: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please sign in to request a custom prescription.");
      nav("/login");
      return;
    }
    if (!form.goals.trim()) {
      toast.error("Please describe your primary goals.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/prescription-request", form);
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit request. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <Header />
      <section className="px-6 sm:px-12 lg:px-20 py-16 max-w-3xl mx-auto">
        <p className="label-eyebrow mb-4">Clinician-tailored prescriptions</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#2D322B] mb-6 leading-tight">
          A formulation made for you.
        </h1>
        <p className="text-base text-[#5C6359] leading-relaxed mb-12">
          Our clinic offers prescription-only supplements compounded in a regulated Australian laboratory —
          not one-size-fits-all bottles, but precise formulations matched to your biomarkers, goals, and physiology.
          Tell us what you need below and Dr Sam&apos;s team will design a protocol for you within 2&ndash;3 business days.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
          {[
            { Icon: Beaker,       label: "Compounded to your level" },
            { Icon: ShieldCheck,  label: "TGA-regulated Australian lab" },
            { Icon: Stethoscope,  label: "Reviewed by a practitioner" },
          ].map(({ Icon, label }) => (
            <div key={label} className="flex items-start gap-3 p-4 bg-white border border-[#E8DFD0]">
              <Icon className="w-5 h-5 text-[#C05C42] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
              <p className="text-sm text-[#2D322B] leading-snug">{label}</p>
            </div>
          ))}
        </div>

        {submitted ? (
          <div data-testid="rx-success" className="bg-white border border-[#E8DFD0] p-8">
            <p className="label-eyebrow mb-3 text-[#C05C42]">Request received</p>
            <h2 className="font-serif text-2xl text-[#2D322B] mb-4">Thank you — we&apos;ve got your request.</h2>
            <p className="text-base text-[#5C6359] leading-relaxed mb-6">
              A confirmation has been emailed to <span className="text-[#2D322B]">{user?.email}</span>.
              Our practitioners will review your goals and biomarker trends, then send you a custom product link
              on badi.com.au within 2&ndash;3 business days.
            </p>
            <p className="text-sm text-[#5C6359] leading-relaxed mb-6">
              Questions in the meantime? Email us at{" "}
              <a href="mailto:badivitality@proton.me" className="text-[#C05C42] hover:text-[#A04A33]" data-testid="rx-contact-email">badivitality@proton.me</a>.
            </p>
            <button
              onClick={() => nav("/dashboard")}
              data-testid="rx-return-dashboard"
              className="btn-primary inline-block"
            >
              Back to dashboard
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-8 bg-white border border-[#E8DFD0] p-8" data-testid="rx-form">
            <Field
              label="Primary goals *"
              hint="What outcome are you hoping for? e.g. better sleep, stable energy, longevity, recovery."
              testid="rx-goals"
            >
              <textarea required rows={3} value={form.goals} onChange={set("goals")}
                className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none text-[#2D322B] resize-none"
                data-testid="rx-goals-input"
              />
            </Field>

            <Field
              label="Symptoms or concerns"
              hint="What's been showing up in your body lately?"
              testid="rx-symptoms"
            >
              <textarea rows={2} value={form.symptoms} onChange={set("symptoms")}
                className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none text-[#2D322B] resize-none"
                data-testid="rx-symptoms-input"
              />
            </Field>

            <Field
              label="Current medications & supplements"
              hint="List everything you're currently taking (so we avoid interactions)."
              testid="rx-current"
            >
              <textarea rows={2} value={form.current_supplements} onChange={set("current_supplements")}
                className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none text-[#2D322B] resize-none"
                data-testid="rx-current-input"
              />
            </Field>

            <Field
              label="Allergies & sensitivities"
              hint="Any ingredients to avoid? (e.g. gluten, soy, dairy, nightshades, specific herbs)"
              testid="rx-allergies"
            >
              <input type="text" value={form.allergies} onChange={set("allergies")}
                className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none text-[#2D322B]"
                data-testid="rx-allergies-input"
              />
            </Field>

            <Field label="Preferred form" hint="" testid="rx-dosage">
              <select value={form.dosage_preference} onChange={set("dosage_preference")}
                className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none text-[#2D322B]"
                data-testid="rx-dosage-input"
              >
                <option value="">Choose…</option>
                {DOSAGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>

            <Field label="Anything else?" hint="Optional — context the practitioner should know." testid="rx-notes">
              <textarea rows={2} value={form.notes} onChange={set("notes")}
                className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none text-[#2D322B] resize-none"
                data-testid="rx-notes-input"
              />
            </Field>

            <p className="text-xs text-[#9A8F7E] leading-relaxed">
              Your most recent biomarkers will be auto-attached to this request for the practitioner&apos;s review.
              Custom-compounded prescriptions are not a substitute for medical care.
            </p>

            <button type="submit" disabled={busy} data-testid="rx-submit" className="btn-primary w-full">
              {busy ? "Sending request…" : "Submit prescription request"}
            </button>

            <div className="pt-6 border-t border-[#E8DFD0]">
              <p className="text-xs uppercase tracking-[2px] text-[#9A8F7E] mb-2">Prefer our store workflow?</p>
              <a
                href={STORE_FALLBACK_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="rx-store-fallback"
                className="inline-flex items-center gap-2 text-sm text-[#C05C42] hover:text-[#A04A33] transition-colors"
              >
                Order via badi.com.au instead <ExternalLink className="w-3 h-3" />
              </a>
              <p className="text-xs text-[#9A8F7E] mt-2 leading-relaxed">
                Note: requests submitted via the store don&apos;t auto-attach your Badi biomarker context — the in-app form above gives our clinical team the full picture for faster, more precise compounding.
              </p>
            </div>
          </form>
        )}
      </section>
      <Footer />
    </div>
  );
}

function Field({ label, hint, testid, children }) {
  return (
    <div data-testid={testid}>
      <label className="label-eyebrow block mb-1">{label}</label>
      {hint && <p className="text-xs text-[#9A8F7E] mb-2">{hint}</p>}
      {children}
    </div>
  );
}
